import { useEffect, useRef, useState } from 'react';
import { startTimer, resetTimer } from '../../api/tournamentsApi';
import { useToast } from '../Toast';
import { IconPlay, IconTimerReset } from './TournamentIcons';

/**
 * Компактный таймер-бар, встраивается в sticky-шапку страницы.
 * Показывает MM:SS обратного отсчёта и кнопку запуска/сброса для canManage.
 */
const TournamentTimer = ({ tournament, canManage, onTournamentUpdate, clientId }) => {
  const { addToast } = useToast();
  const [timerLoading, setTimerLoading] = useState(false);
  // null = таймер не запущен, иначе — оставшиеся секунды
  const [remaining, setRemaining] = useState(null);
  // Показывать ли модалку "Время вышло"
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  // Флаг: уведомление о завершении уже было показано для текущего timerStartedAt
  const notifiedRef = useRef(false);

  const durationSec    = (tournament.matchDurationMinutes || 0) * 60;
  const timerStartedAt = tournament.timerStartedAt; // ISO-строка Instant

  // Сбрасываем флаг уведомления при каждом новом запуске таймера
  useEffect(() => {
    notifiedRef.current = false;
    setShowExpiredModal(false);
  }, [timerStartedAt]);

  // Пересчитываем remaining при монтировании и каждую секунду
  useEffect(() => {
    const calc = () => {
      if (!timerStartedAt) { setRemaining(null); return; }
      const elapsed = Math.floor((Date.now() - new Date(timerStartedAt).getTime()) / 1000);
      const left = Math.max(0, durationSec - elapsed);
      setRemaining(left);

      // Срабатывает ровно один раз при истечении таймера
      if (left === 0 && !notifiedRef.current) {
        notifiedRef.current = true;
        setShowExpiredModal(true);
        // Вибрация через Telegram WebApp HapticFeedback
        try {
          window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
        } catch (_) { /* вне Telegram — молча игнорируем */ }
      }
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [timerStartedAt, durationSec]);

  const handleStart = async () => {
    setTimerLoading(true);
    try {
      const updated = await startTimer(tournament.publicId, clientId);
      onTournamentUpdate(updated);
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setTimerLoading(false);
    }
  };

  const handleReset = async () => {
    setTimerLoading(true);
    try {
      const updated = await resetTimer(tournament.publicId, clientId);
      onTournamentUpdate(updated);
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setTimerLoading(false);
    }
  };

  const isActive  = timerStartedAt !== null && timerStartedAt !== undefined;
  const isExpired = isActive && remaining === 0;

  // Форматирование MM:SS
  const fmt = (sec) => {
    const total = sec !== null ? sec : durationSec;
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Цвет: зелёный → янтарный (≤20%) → красный (истёк)
  const pct        = remaining !== null && durationSec > 0 ? remaining / durationSec : 1;
  const timerColor = isExpired ? '#dc2626' : pct <= 0.2 ? '#d97706' : '#16a34a';

  // Фон-полоска прогресса (ширина соответствует оставшемуся времени)
  const progressPct = isActive && durationSec > 0
    ? Math.max(0, Math.min(100, (remaining / durationSec) * 100))
    : 100;

  return (
    <div style={{
      background: '#fff',
      borderBottom: `2px solid ${isExpired ? '#fecaca' : '#f0f0f5'}`,
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Полоска прогресса снизу */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, height: 2,
        width: `${progressPct}%`,
        background: timerColor,
        transition: 'width 1s linear, background 0.3s',
      }} />

      {/* Иконка часов */}
      <div style={{ flexShrink: 0, color: '#9ca3af' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </div>

      {/* Лейбл */}
      <div style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>
        Раунд
      </div>

      {/* MM:SS */}
      <div style={{
        fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        color: timerColor, lineHeight: 1, letterSpacing: 1,
        transition: 'color 0.3s', flex: 1,
      }}>
        {fmt(remaining)}
      </div>

      {/* Статус текст */}
      <div style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
        {isExpired
          ? <span style={{ color: '#dc2626', fontWeight: 700 }}>Время вышло</span>
          : isActive
            ? 'идёт'
            : `${tournament.matchDurationMinutes} мин`}
      </div>

      {/* Кнопки (только canManage) */}
      {canManage && (
        !isActive ? (
          <button
            onClick={handleStart}
            disabled={timerLoading}
            style={{
              padding: '7px 14px', borderRadius: 8, flexShrink: 0,
              border: '1.5px solid #86efac',
              background: timerLoading ? '#f0fdf4' : '#dcfce7',
              color: '#15803d', fontSize: 13, fontWeight: 700,
              cursor: timerLoading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <IconPlay />
            Старт
          </button>
        ) : (
          <button
            onClick={handleReset}
            disabled={timerLoading}
            style={{
              padding: '7px 12px', borderRadius: 8, flexShrink: 0,
              border: '1.5px solid #e5e7eb',
              background: timerLoading ? '#f9fafb' : '#f3f4f6',
              color: '#6b7280', fontSize: 13, fontWeight: 600,
              cursor: timerLoading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            <IconTimerReset />
            Сброс
          </button>
        )
      )}

      {/* Модалка "Время вышло" */}
      {showExpiredModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 500,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 24px',
        }}>
          <div style={{
            background: '#fff', borderRadius: 18,
            padding: '28px 24px 20px',
            width: '100%', maxWidth: 320,
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 12 }}>⏰</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
              Время вышло!
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
              Раунд завершён — {tournament.matchDurationMinutes} мин истекли.
            </div>
            <button
              onClick={() => setShowExpiredModal(false)}
              style={{
                width: '100%', padding: '13px',
                borderRadius: 12, border: 'none',
                background: '#dc2626', color: '#fff',
                fontSize: 15, fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentTimer;
